/**
 * Wave RRRR (2026-05-16) · PM-facing notification bell.
 *
 * Bell icon in TopBar with unread count badge.
 * Click → dropdown panel (NotificationDropdown).
 * Auto-refreshes unread count every 60s while mounted.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/store/auth.store';
import { api } from '@/api/client';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';
import { NotificationDropdown } from './NotificationDropdown';

const POLL_INTERVAL_MS = 60_000;

export function NotificationsBell() {
  const { t } = useT();
  const user = useAuth((s) => s.user);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const result = await api.listUserNotifications({ unreadOnly: true, limit: 100 });
      setUnread(result.notifications.length);
    } catch {
      // Silently ignore — badge will update on next tick.
    }
  }, [user]);

  // Poll unread count every 60s.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const tick = async () => {
      if (alive) await fetchUnread();
    };
    void tick();
    const id = window.setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [user, fetchUnread]);

  // Outside-click + ESC to close.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;

  const badgeCount = Math.min(unread, 99);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t.notificationsBell.openBell}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'pressable focus-ring relative flex h-11 w-11 items-center justify-center rounded-full',
          'text-ink-muted hover:bg-surface-2 hover:text-ink transition-colors duration-150',
          open && 'bg-surface-2 text-ink',
        )}
      >
        {/* Bell icon */}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {/* Unread badge — listen-to-JP-fix · 2026-05-16
            JP keeps catching this bleeding out of the button. The previous
            `top-0 right-0` anchor the badge at the outer corner, so half
            of the badge still sat OUTSIDE the button bounds. Use positive
            inset (top:1 right:1) so the badge actually fits inside the
            tappable area, and shrink it slightly so it doesn't feel huge.
            Also drop the white ring against surface — looks like a sticker. */}
        {badgeCount > 0 && (
          <span
            aria-hidden="true"
            className={cn(
              'absolute top-1 right-1 inline-flex h-[14px] min-w-[14px] items-center justify-center rounded-full',
              'bg-err text-white text-[9px] font-bold leading-none px-[3px]',
              'animate-[scale-in_0.15s_ease-out]',
            )}
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>

      {open && (
        <NotificationDropdown
          onClose={() => setOpen(false)}
          onCountChange={setUnread}
        />
      )}
    </div>
  );
}
